import React, { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "app/slice/selectors";

import { Label } from "app/components/ui/label";
import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "app/components/ui/dialog";

interface Props {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  apiResponse?: any;
  isFromDetailsPage?: boolean;
  fullAddress?: string;
  isActivation?: boolean;
  onContinue?: () => void;
  onGetStarted?: () => void;
  congratsCloseSameAsGetStarted?: boolean;
  activationLoggedInCtaLabel?: string;
  onActivationLoggedInCta?: () => void;
}

export const CongratulationsDialog = ({
  showDialog,
  setShowDialog,
  apiResponse,
  isFromDetailsPage,
  fullAddress,
  isActivation,
  onContinue,
  onGetStarted,
  congratsCloseSameAsGetStarted,
  activationLoggedInCtaLabel,
  onActivationLoggedInCta,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleCloseClick = useCallback(() => {
    if (congratsCloseSameAsGetStarted && onGetStarted) {
      onGetStarted();
    }
    setShowDialog(false);
  }, [congratsCloseSameAsGetStarted, onGetStarted, setShowDialog]);

  // Get user from Redux (this is still needed for other parts of the app)
  const user = useSelector(selectUser);

  // Format address from API response
  const formatAddress = () => {
    if (fullAddress) {
      return fullAddress;
    }

    if (
      !apiResponse?.data?.service_availability?.wholeSaleApi?.wholeSaleResponse
        ?.result
    ) {
      return "";
    }

    const result =
      apiResponse.data.service_availability.wholeSaleApi.wholeSaleResponse
        .result;
    const city = result.city || "";
    const state = result.state || "";
    const zipCode = result.zipCode || "";

    return `${city}, ${state} ${zipCode}`.trim();
  };

  const formattedAddress = formatAddress();

  const isLoggedIn = Boolean(user?._id || user?.id);
  const useActivationDashboardCta =
    Boolean(isActivation) &&
    isLoggedIn &&
    Boolean(activationLoggedInCtaLabel && onActivationLoggedInCta);

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent
        className="top-1/2 gap-0 bg-white w-[95%] sm:max-w-[33.5rem] p-4 sm:p-5 lg:p-6"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Close button */}
        <Button
          type="button"
          variant="ghost"
          className="absolute top-4 right-4 z-10 p-0 hover:bg-transparent hover:brightness-50 rounded-full focus:ring-2 focus:ring-[#1D1D1B]"
          onClick={handleCloseClick}
          aria-label="Close dialog"
        >
          <img
            src="/assets/close-circle.webp"
            className="size-5 md:size-6"
            alt="Close"
          />
        </Button>
        <DialogHeader className="relative flex justify-center items-center p-0 mb-4 bg-transparent md:mb-5">
          <div className="flex-shrink-0 size-10 sm:size-11 md:size-12 lg:size-[3.125rem] mb-2 sm:mb-3 lg:mb-4">
            <img
              src="/assets/green-tick.svg"
              className="w-full h-auto"
              alt="Tick"
            />
          </div>
          <DialogTitle className="pb-1 text-xl font-bold leading-6 text-black lg:text-2xl md:leading-7 font-mobile">
            Congratulations!
          </DialogTitle>
          <DialogDescription className="text-[0.825rem] sm:text-sm font-medium leading-4 sm:leading-5 text-[#3C3C3C] pb-2 md:pb-[0.625rem]">
            You have VLE mobile {isActivation ? "" : "fixed phone"} coverage
          </DialogDescription>
          {isFromDetailsPage ? (
            <DialogDescription className="text-[0.825rem] sm:text-sm font-medium leading-4 sm:leading-5 text-[#3C3C3C] pb-2 md:pb-[0.625rem]">
              click <span className="text-[#FE4D01]">"Continue"</span> to begin
              setting up your new fixed phone system.
            </DialogDescription>
          ) : (
            <DialogDescription className="font-playfair text-center capitalize text-[0.925rem] sm:text-base font-bold leading-5 md:leading-[1.375rem] tracking-[-0.03rem] text-black">
              {isActivation ? (
                useActivationDashboardCta ? (
                  <>
                    Click{" "}
                    <span className="text-[#FE4D01]">
                      {activationLoggedInCtaLabel}
                    </span>{" "}
                    to continue activation in fixed phone SIM Service.
                  </>
                ) : isLoggedIn ? (
                  <>
                    Click <span className="text-[#FE4D01]">"Continue"</span> to
                    proceed with activation.
                  </>
                ) : (
                  <>
                    Sign in to your existing account or click{" "}
                    <span className="text-[#FE4D01]">"Continue"</span> to
                    proceed with activation.
                  </>
                )
              ) : user?._id || user?.id ? (
                <>
                  Click <span className="text-[#FE4D01]">"Get Started"</span> to
                  begin setting up your new fixed phone system.
                </>
              ) : (
                <>
                  Sign in to your existing account or click{" "}
                  <span className="text-[#FE4D01]">"Get Started"</span> to begin
                  setting up your new fixed phone system.
                </>
              )}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="">
          <Label
            htmlFor="address"
            className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-1.5"
          >
            Address Entered
          </Label>
          <Input
            id="address"
            type="text"
            value={formattedAddress}
            className="bg-white h-10 md:h-[2.875rem] p-2 sm:p-2.5 md:p-3 border-[#D1D5DB] rounded-xl text-black text-[0.825rem] sm:text-sm font-semibold leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F]"
            maxLength={50}
            disabled
          />
        </div>
        <span className="inline-block text-[0.825rem] md:text-sm font-medium leading-[1.125rem] md:leading-5 text-[#3C3C3C] mt-2 md:mt-2.5 mb-3 md:mb-4">
          {!isFromDetailsPage && !(user?._id || user?.id)
            ? "Make a selection below to continue"
            : "Click below to continue"}
        </span>
        {isFromDetailsPage || useActivationDashboardCta ? (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="primary"
              className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] 2xl:h-[3.875rem] 2xl:text-lg font-semibold"
              onClick={() => {
                if (useActivationDashboardCta) {
                  onActivationLoggedInCta?.();
                }
                setShowDialog(false);
              }}
            >
              {isFromDetailsPage ? "Continue" : activationLoggedInCtaLabel}
            </Button>
          </div>
        ) : (
          <div
            className={`grid gap-2.5 md:gap-3 ${
              user?._id || user?.id ? "" : "grid-cols-2"
            }`}
          >
            <Button
              type="button"
              variant="primary"
              className={`h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] 2xl:h-[3.875rem] 2xl:text-lg font-semibold ${
                user?._id || user?.id ? "hidden" : ""
              }`}
              onClick={() => {
                const currentPath = location.pathname + location.search;

                const redirectState = {
                  congratulationsData: {
                    apiResponse,
                    fullAddress,
                    isFromDetailsPage,
                    isActivation,
                    timestamp: Date.now(),
                  },
                };

                setShowDialog(false);
                navigate("/login", {
                  state: {
                    redirectTo: currentPath,
                    redirectState,
                  },
                });
              }}
            >
              Sign in
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] 2xl:h-[3.875rem] 2xl:text-lg font-semibold"
              onClick={() => {
                if (isActivation && onContinue) {
                  onContinue();
                } else if (!isActivation) {
                  if (onGetStarted) {
                    onGetStarted();
                  } else {
                    navigate("/fixed-phone/list-page");
                  }
                }
                setShowDialog(false);
              }}
            >
              {isActivation ? "Continue" : "Get Started"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
